// Finds passages in a PDF page's text layer and marks them up for react-pdf's
// `customTextRenderer`. The passage is the page's own words, but it rarely
// lines up with the text layer character for character: items split words and
// lines arbitrarily, spacing and quote styles differ, and a word broken at a
// line end carries a hyphen. So both sides are folded the same way before
// searching — whitespace and dashes dropped, case and quote styles unified —
// and every folded character remembers where it came from, so a match maps
// back to exact character ranges in the items (a passage can start and end
// mid-item and span many).

// The subset of pdf.js' TextItem / TextMarkedContent this needs: marked
// content has no `str` and renders no text.
export type TextLayerItem = { str?: string };

// [start, end) character offsets within one item's `str`.
export type ItemRange = [number, number];

// Ranges to highlight, by item index (the index into `TextContent.items`
// react-pdf passes to `customTextRenderer` as `itemIndex`).
export type ItemHighlights = Map<number, ItemRange[]>;

const SINGLE_QUOTES = /[‘’‚‛′`´]/g;
const DOUBLE_QUOTES = /[“”„‟″«»]/g;
// Hyphens and dashes of every style (incl. the soft hyphen and minus sign).
// Dropped outright: that makes a word hyphenated at a line end match its
// unbroken spelling, and dash styles match each other.
const DASHES_AND_SPACE = /[\s­‐-―−-]/;
const ALL_DASHES_AND_SPACE = new RegExp(DASHES_AND_SPACE.source, 'g');

// Folds one source character to zero or more comparable characters.
function foldChar(char: string): string {
  if (DASHES_AND_SPACE.test(char)) return '';
  return char
    .normalize('NFKC')
    .toLowerCase()
    .replace(SINGLE_QUOTES, "'")
    .replace(DOUBLE_QUOTES, '"')
    .replace(ALL_DASHES_AND_SPACE, '');
}

export function foldText(text: string): string {
  return Array.from(text, foldChar).join('');
}

type Origin = { itemIndex: number; offset: number };

// The folded text of all items, and for each folded character the item and
// UTF-16 offset of the source character it came from.
function foldItems(items: TextLayerItem[]): {
  folded: string;
  origins: Origin[];
} {
  let folded = '';
  const origins: Origin[] = [];
  items.forEach(({ str }, itemIndex) => {
    if (!str) return;
    let offset = 0;
    Array.from(str).forEach((char) => {
      const out = foldChar(char);
      for (let i = 0; i < out.length; i += 1) {
        origins.push({ itemIndex, offset });
      }
      folded += out;
      offset += char.length;
    });
  });
  return { folded, origins };
}

function addRange(
  highlights: ItemHighlights,
  itemIndex: number,
  range: ItemRange,
) {
  const ranges = highlights.get(itemIndex) ?? [];
  ranges.push(range);
  highlights.set(itemIndex, ranges);
}

// Sorts and merges overlapping/adjacent ranges of each item, in place.
function mergeRanges(highlights: ItemHighlights): ItemHighlights {
  highlights.forEach((ranges, itemIndex) => {
    const merged = [...ranges]
      .sort((a, b) => a[0] - b[0])
      .reduce<ItemRange[]>((acc, range) => {
        const last = acc[acc.length - 1];
        if (last && range[0] <= last[1]) {
          last[1] = Math.max(last[1], range[1]);
        } else {
          acc.push([range[0], range[1]]);
        }
        return acc;
      }, []);
    highlights.set(itemIndex, merged);
  });
  return highlights;
}

// Finds each passage (first occurrence) in the items. Returns the ranges to
// highlight and the passages that had no match (e.g. a scanned page with no
// text layer) — those fall back to their regions.
export function findPassages(
  items: TextLayerItem[],
  passages: string[],
): { highlights: ItemHighlights; unmatched: number[] } {
  const highlights: ItemHighlights = new Map();
  const unmatched: number[] = [];
  const { folded, origins } = foldItems(items);
  passages.forEach((passage, passageIndex) => {
    const needle = foldText(passage);
    const start = needle ? folded.indexOf(needle) : -1;
    if (start === -1) {
      unmatched.push(passageIndex);
      return;
    }
    const first = origins[start];
    const last = origins[start + needle.length - 1];
    const lastChar = items[last.itemIndex].str?.codePointAt(last.offset) ?? 0;
    const lastEnd = last.offset + (lastChar > 0xffff ? 2 : 1);
    for (let i = first.itemIndex; i <= last.itemIndex; i += 1) {
      const str = items[i].str ?? '';
      if (str) {
        addRange(highlights, i, [
          i === first.itemIndex ? first.offset : 0,
          i === last.itemIndex ? lastEnd : str.length,
        ]);
      }
    }
  });
  return { highlights: mergeRanges(highlights), unmatched };
}

const escapeHtml = (text: string) =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

// The HTML react-pdf puts in an item's text-layer span: the escaped text, with
// the given ranges wrapped in `<mark class={markClassName}>`. The text comes
// from the PDF, so every piece is escaped.
export function renderHighlightedText(
  str: string,
  ranges: ItemRange[] | undefined,
  markClassName: string,
): string {
  if (!ranges?.length) return escapeHtml(str);
  let html = '';
  let cursor = 0;
  ranges.forEach(([start, end]) => {
    html += escapeHtml(str.slice(cursor, start));
    html += `<mark class="${escapeHtml(markClassName)}">${escapeHtml(
      str.slice(start, end),
    )}</mark>`;
    cursor = end;
  });
  return html + escapeHtml(str.slice(cursor));
}

// Whether regions measured on a page of `expectedRatio` (width ÷ height) can
// be drawn on a page rendered at `width` × `height`: the BE asks to skip them
// when the ratios differ by more than about 1%.
export function canDrawRegions(
  expectedRatio: number | null | undefined,
  width: number,
  height: number,
): boolean {
  if (!expectedRatio || !width || !height) return false;
  return Math.abs(width / height - expectedRatio) / expectedRatio <= 0.01;
}
